import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import api from '../../../api';
import { showAlertForError } from '../../../actions/alerts';
import CloseIcon from '@/material-icons/400-24px/close.svg?react';
import { Icon } from 'mastodon/components/icon';

const messages = defineMessages({
  title: { id: 'epsilon.category_suggestions.title', defaultMessage: 'Discover topics' },
  subscribe: { id: 'epsilon.category_suggestions.subscribe', defaultMessage: 'Follow' },
  subscribersCount: { id: 'epsilon.category_suggestions.subscribers_count', defaultMessage: '{count, plural, one {# subscriber} other {# subscribers}}' },
  dismiss: { id: 'epsilon.category_suggestions.dismiss', defaultMessage: 'Dismiss' },
});

const STORAGE_KEY = 'epsilon_category_suggestions_dismissed';

const CategorySuggestionTile = ({ category, onSubscribe, intl }) => {
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleSubscribe = useCallback(async () => {
    setIsSubscribing(true);
    try {
      await onSubscribe(category.id);
    } finally {
      setIsSubscribing(false);
    }
  }, [category.id, onSubscribe]);

  return (
    <div className='epsilon-category-suggestion-tile'>
      <div className='epsilon-category-suggestion-tile__content'>
        <div className='epsilon-category-suggestion-tile__name'>{category.name}</div>
        <div className='epsilon-category-suggestion-tile__stats'>
          {intl.formatMessage(messages.subscribersCount, { count: category.subscribers_count })}
        </div>
      </div>
      <button
        className='epsilon-category-suggestion-tile__button'
        onClick={handleSubscribe}
        disabled={isSubscribing}
      >
        {intl.formatMessage(messages.subscribe)}
      </button>
    </div>
  );
};

const EpsilonCategorySuggestions = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDismissed, setIsDismissed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      console.error('Failed to save dismiss state:', error);
    }
  }, []);

  useEffect(() => {
    if (isDismissed) {
      return;
    }
    const fetchSuggestions = async () => {
      try {
        const response = await api().get('/api/v1/epsilon/categorization/categories/suggested');

        const responseData = response?.data || response;
        setSuggestions(Array.isArray(responseData) ? responseData : []);

      } catch (error) {
        console.error('Erreur API suggestions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSuggestions();
  }, [dispatch]);

  const handleSubscribe = useCallback(async (categoryId) => {
    try {
      const currentResponse = await api().get('/api/v1/epsilon/categorization/subscriptions');

      const currentData = currentResponse?.data || currentResponse;
      const currentSubscriptions = Array.isArray(currentData) ? currentData : [];

      const updatedSubscriptions = [...currentSubscriptions, categoryId];

      await api().put('/api/v1/epsilon/categorization/subscriptions', {
          category_ids: updatedSubscriptions,
        });

      setSuggestions(prev => prev.filter(cat => cat.id !== categoryId));
    } catch (error) {
      dispatch(showAlertForError(error));
    }
  }, [dispatch]);

  if (isDismissed || isLoading || suggestions.length === 0) {
    return null;
  }

  return (
    <div className='epsilon-category-suggestions'>
      <div className='epsilon-category-suggestions__header'>
        <h3 className='epsilon-category-suggestions__title'>
          {intl.formatMessage(messages.title)}
        </h3>
        <button
          className='epsilon-category-suggestions__close'
          onClick={handleDismiss}
          aria-label={intl.formatMessage(messages.dismiss)}
          title={intl.formatMessage(messages.dismiss)}
        >
          <Icon id='times' icon={CloseIcon} />
        </button>
      </div>
      <div className='epsilon-category-suggestions__list'>
        {suggestions.map(category => (
          <CategorySuggestionTile
            key={category.id}
            category={category}
            onSubscribe={handleSubscribe}
            intl={intl}
          />
        ))}
      </div>
    </div>
  );
};

export default EpsilonCategorySuggestions;
